// Centralized API endpoint definitions grouped by domain.

const requireClientId = (clientId) => {
  const numericClientId = Number(clientId);
  if (!Number.isFinite(numericClientId) || numericClientId <= 0) {
    throw new Error("A valid client id is required for this endpoint.");
  }
  return encodeURIComponent(numericClientId);
};

export const API_ENDPOINTS = {
  // Health & System
  HEALTH: {
    INFO: "/",
    SERVICE: "/health",
    DATABASE: "/health/db",
    EMAIL: "/health/email",
    FILE_STORAGE: "/health/file-storage",
    NOTIFICATION: "/health/notification",
    STRIPE: "/health/stripe",
    CDN: "/health/cdn",
    READINESS: "/ready",
    LIVENESS: "/live"
  },

  // Authentication
  AUTH: {
    REGISTER: "/auth/register",
    LOGIN: "/auth/login",
    // TWO_FA_SEND_CODE: "/auth/two-fa/send-code", //Not being used (current 2FA flow is via authenticator app, not SMS/email)
    TWO_FA_SETUP: "/auth/two-fa/setup",
    TWO_FA_VERIFY: "/auth/two-fa/verify",
    REQUEST_RESET_PASSWORD: "/auth/request-reset-password",
    RESET_PASSWORD: "/auth/reset-password",
    CHANGE_PASSWORD: "/auth/change-password",
    ME: "/auth/me",
  },

  // Logs
  LOGS: {
    CREATE: "/logs",
    LIST_BY_CLIENT: (clientId) =>
      `/logs/client/${requireClientId(clientId)}`,
    UPDATE_BY_CLIENT_LOG_ID: (id) => `/logs/client/${encodeURIComponent(id)}`,
  },

  // Clients + Notifications Settings
  CLIENTS: {
    LIST: "/clients",
    PUBLIC_LIST: "/clients",
    CREATE: "/clients",
    GET_BY_ID: (id) => `/clients/id/${encodeURIComponent(id)}`,
    GET_BY_EMAIL: (email) => `/clients/email/${encodeURIComponent(email)}`,
    PARSER: "/clients/parser",
    UPDATE: (id) => `/clients/${encodeURIComponent(id)}`,
    DELETE: (id) => `/clients/${encodeURIComponent(id)}`,
  },

  // Whitelisted IPs
  WHITELISTED_IPS: {
    CREATE: "/whitelisted-ips",
    LIST_BY_CLIENT: (clientId, limit = 100, offset = 0) =>
      `/whitelisted-ips/client/${encodeURIComponent(clientId)}?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`,
    GET_BY_ID: (id) => `/whitelisted-ips/id/${encodeURIComponent(id)}`,
    UPDATE_BY_ID: (id) => `/whitelisted-ips/id/${encodeURIComponent(id)}`,
    DELETE_BY_ID: (id) => `/whitelisted-ips/id/${encodeURIComponent(id)}`,
  },

  // Users
  USERS: {
    CREATE: "/users",
    GET_BY_ID: (id) => `/users/id/${encodeURIComponent(id)}`,
    GET_BY_CLIENT: (clientId) =>
      `/users/client/${encodeURIComponent(clientId)}`,
    GET_BY_EMAIL: (email) => `/users/email/${encodeURIComponent(email)}`,
    UPDATE: (id) => `/users/id/${encodeURIComponent(id)}`,
    DELETE: (id) => `/users/id/${encodeURIComponent(id)}`,
  },

  // Payment Methods
  PAYMENT_METHODS: {
    LIST_BY_CLIENT: (clientId) =>
      `/payment-methods/client/${requireClientId(clientId)}`,
    CREATE: "/payment-methods",
    GET_BY_ID: (id) => `/payment-methods/id/${encodeURIComponent(id)}`,
    UPDATE: (id) => `/payment-methods/${encodeURIComponent(id)}`,
    DELETE: (id) => `/payment-methods/${encodeURIComponent(id)}`,
  },

  // Invoices
  INVOICES: {
    LIST_BY_CLIENT: (clientId) =>
      `/invoices/client/${requireClientId(clientId)}`,
    GET_BY_ID: (id) => `/invoices/id/${encodeURIComponent(id)}`,
  },

  // Subscription Plans
  SUBSCRIPTION_PLANS: {
    CREATE: "/subscription-plans",
    CUSTOM_PLAN: "/custom-plan",
    LIST: (limit = 20, offset = 0) =>
      `/subscription-plans?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`,
    GET_BY_ID: (id) => `/subscription-plans/id/${encodeURIComponent(id)}`,
    GET_BY_NAME: (name) => `/subscription-plans/name/${encodeURIComponent(name)}`,
    UPDATE_BY_ID: (id) => `/subscription-plans/id/${encodeURIComponent(id)}`,
    DELETE_BY_ID: (id) => `/subscription-plans/id/${encodeURIComponent(id)}`,
  },

  // Referrals
  REFERRALS: {
    CREATE: "/referrals",
    LIST_BY_CLIENT: (clientId, limit = 20, offset = 0) =>
      `/referrals/client/${encodeURIComponent(clientId)}?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`,
    GET_BY_ID: (id) => `/referrals/id/${encodeURIComponent(id)}`,
    UPDATE_BY_ID: (id) => `/referrals/id/${encodeURIComponent(id)}`,
    DELETE_BY_ID: (id) => `/referrals/id/${encodeURIComponent(id)}`,
  },

  // Support Tickets
  SUPPORT_TICKETS: {
    CREATE: "/support-tickets",
    LIST_BY_CLIENT: (clientId, limit = 20, offset = 0, status) => {
      const basePath = `/support-tickets/client/${encodeURIComponent(clientId)}`;
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });

      if (status) {
        params.set("status", String(status));
      }

      return `${basePath}?${params.toString()}`;
    },
    GET_BY_ID: (id) => `/support-tickets/id/${encodeURIComponent(id)}`,
    UPDATE_BY_ID: (id) => `/support-tickets/id/${encodeURIComponent(id)}`,
    DELETE_BY_ID: (id) => `/support-tickets/id/${encodeURIComponent(id)}`,
  },

  // Ticket Messages
  TICKET_MESSAGES: {
    CREATE: "/ticket-messages",
    LIST_BY_TICKET: (ticketId, limit = 20, offset = 0) =>
      `/ticket-messages/ticket/${encodeURIComponent(ticketId)}?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`,
    GET_BY_ID: (id) => `/ticket-messages/id/${encodeURIComponent(id)}`,
    GET_ATTACHMENT_BY_MESSAGE_ID: (id) =>
      `/ticket-messages/attachment/message/${encodeURIComponent(id)}`,
    UPDATE_BY_ID: (id) => `/ticket-messages/id/${encodeURIComponent(id)}`,
    DELETE_BY_ID: (id) => `/ticket-messages/id/${encodeURIComponent(id)}`,
  },

  // Phone numbers
  PHONE_NUMBERS: {
    SHOW_AVAILABLE: "/show-phone-numbers",
    BUY: "/buy-phone-number",
    LIST_BY_CLIENT: (clientId) =>
      `/phone-numbers/client/${requireClientId(clientId)}`,
    GET_BY_ID: (id) => `/phonenumber/id/${encodeURIComponent(id)}`,
    UPDATE_BY_ID: (id) => `/phonenumber/id/${encodeURIComponent(id)}`,
    GREETING_BY_ID: (id) => `/phonenumber/greeting/id/${encodeURIComponent(id)}`,
    RELEASE_BY_NUMBER: (phoneNumber) =>
      `/phonenumber/number/${encodeURIComponent(phoneNumber)}`,
  },
};

export default API_ENDPOINTS;
